import { useTranslation } from 'next-i18next';
import React, { useEffect } from 'react';

import {
  Container, Grid, Typography, useMediaQuery,
} from '@mui/material';

import { AsyncCheckoutService, GiftCardOption, ITheme } from '@klangio/react-shared-elements';
import { makeStyles } from 'tss-react/mui';
import { ECARDS_AMOUNTS } from '../../constants';
import EcardsAmountItem from '../atoms/EcardsAmountItem';
import EcardsDesignItem from '../atoms/EcardsDesignItem';
import EcardsPreview from '../atoms/EcardsPreview';
import GiftCardForm from '../molecules/GiftCardForm';

const useStyles = makeStyles()(() => ({
  app: {
    textAlign: 'center',
    color: '#FFF',
  },
  title: {
    marginTop: 30,
    fontSize: '2.8em',
    lineHeight: '1.15em',
    marginBottom: 30,
    fontWeight: 400,
  },
  lead: {
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '80%',
    marginTop: '1.13em',
    marginBottom: '1.13em',
    fontWeight: 400,
    fontSize: '1.3em',
    lineHeight: '1.6em',
  },
  stepTitle: {
    paddingTop: 50,
    fontSize: '28px',
    lineHeight: '1.15em',
    marginBottom: 30,
    fontWeight: 400,
    color: '#ffffff',
  },
  cta: {
    marginTop: 30,
  },
  container: {
    // workaround for small mobile screens
    '@media screen and (max-width: 400px)': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
}));

// image size: 1968x906
export const ECARDS_DESIGN_IMAGES = [
  'https://klang.io/wp-content/uploads/2023/11/cover_xmas_3.jpg',
  'https://klang.io/wp-content/uploads/2023/11/cover_xmas_1.jpg',
  'https://klang.io/wp-content/uploads/2023/11/cover_xmas_2.jpg',
  'https://klang.io/wp-content/uploads/2023/11/cover_birthday_2.jpg',
  'https://klang.io/wp-content/uploads/2023/11/cover_birthday_1.jpg',
  'https://klang.io/wp-content/uploads/2023/11/cover_casual_1.jpg',
];

const GiftCardConfiguratorWidget: React.FC = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [submitted, setSubmitted] = React.useState(false);
  const [eCards, setECards] = React.useState(ECARDS_AMOUNTS);
  const [design, setDesign] = React.useState(0);
  const [amount, setAmount] = React.useState(50);
  const [senderName, setSenderName] = React.useState('Mozart');
  const recipientName = 'Beethoven';
  const [message, setMessage] = React.useState<GiftCardOption['message']>(
    t('ecard.defaultMessageText'),
  );
  const isMobile = useMediaQuery((theme: ITheme) => theme.breakpoints.down('sm'));

  const setPrices = async () => {
    const newECards: any = [];
    try {
      await Promise.all(
        eCards.map(async (item: any) => {
          const price = await AsyncCheckoutService.getPrice(item.productId);
          const eCardWithNewPrice = { ...item, price };
          newECards.push(eCardWithNewPrice);
        }),
      );
      if (newECards.length > 0) {
        newECards.sort((a: any, b: any) => a.amount - b.amount);
        setECards(newECards);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    setPrices().catch((err) => console.log(err));
  }, []);

  const handleSubmit = async () => {
    setSubmitted(true);
    const selectedECard = eCards.find((item: any) => item.amount === amount);
    const data = {
      coverImage: ECARDS_DESIGN_IMAGES[design],
      amount: selectedECard.amount,
      senderName,
      recipientName,
      message,
    };
    try {
      const order = await AsyncCheckoutService.buyGiftCard(
        data,
        selectedECard.productId,
        `${t('ecard.productName')} (${amount})`,
      );
      window.location.href = `https://ai2notes.klang.io/api/Ecard/Delivery?order_id=${order.order_id}`;
    } catch (reason) {
      console.log(reason);
      setSubmitted(false);
    }
  };

  return (
    <section id="section-ecard">
      <Container maxWidth="md">
        <Typography id="section-ecard" className={classes.stepTitle} variant="h2" component="h2">
          {t('ecard.step1heading')}
        </Typography>
        <Grid container spacing={2} className={classes.container}>
          {eCards.map((ec: any) => (
            <Grid key={`${ec.amount}-ecard`} item xs={12} md={3}>
              <EcardsAmountItem
                amount={ec.amount}
                price={ec.price}
                color={ec.color}
                discount={ec.discount}
                selected={amount === ec.amount}
                onClick={() => {
                  setAmount(ec.amount);
                }}
              />
            </Grid>
          ))}
        </Grid>

        <Typography className={classes.stepTitle} variant="h2" component="h2">
          {t('ecard.step2heading')}
        </Typography>
        <Grid container spacing={2} className={classes.container}>
          {ECARDS_DESIGN_IMAGES.map((designImage, i) => (
            <Grid key={`${designImage}-ecard`} item xs={12} md={3}>
              <EcardsDesignItem
                designImage={designImage}
                selected={design === i}
                onClick={() => {
                  setDesign(i);
                }}
              />
            </Grid>
          ))}
        </Grid>

        <Typography className={classes.stepTitle} variant="h2" component="h2">
          {t('ecard.step3heading')}
        </Typography>
        <Grid container justifyContent="center" spacing={2}>
          <Grid item xs={12} sm={12} md={6}>
            <GiftCardForm
              submitted={submitted}
              defaultFormData={{
                senderName,
                message,
              }}
              onFormDataChanged={(data) => {
                setSenderName(data.senderName);
                setMessage(data.message);
              }}
              onSubmit={handleSubmit}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            {isMobile ? (
              <EcardsPreview
                disableHoverEffect
                templateUrl="https://ai2notes.klang.io/api/Ecard/template"
                amount={amount}
                senderName={senderName}
                recipientName={recipientName}
                message={message}
                coverImageUrl={ECARDS_DESIGN_IMAGES[design]}
              />
            ) : (
              <EcardsPreview
                disableHoverEffect={false}
                templateUrl="https://ai2notes.klang.io/api/Ecard/template"
                amount={amount}
                senderName={senderName}
                recipientName={recipientName}
                message={message}
                coverImageUrl={ECARDS_DESIGN_IMAGES[design]}
              />
            )}
          </Grid>
        </Grid>
      </Container>
    </section>
  );
};

export default GiftCardConfiguratorWidget;
